import React, { useState } from 'react';
import Input from 'components/ui/Input';
import Select from 'components/ui/Select';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';
import emailjs from '@emailjs/browser';

const PaymentSection = ({ formData, onUpdate, onNext, onBack, bookingSummary }) => {
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showBnplInfo, setShowBnplInfo] = useState(false);

  const cardTypeOptions = [
    { value: 'visa', label: 'Visa' },
    { value: 'mastercard', label: 'Mastercard' },
    { value: 'amex', label: 'American Express' },
    { value: 'rupay', label: 'RuPay' }
  ];

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1)?.padStart(2, '0'),
    label: String(i + 1)?.padStart(2, '0')
  }));

  const yearOptions = Array.from({ length: 10 }, (_, i) => {
    const year = new Date()?.getFullYear() + i;
    return { value: String(year), label: String(year) };
  });

  const handlePayment = async () => {
    setIsProcessing(true);

    if (paymentMethod === 'bnpl') {
      setTimeout(async () => {
        onUpdate({ paymentMethod: 'bnpl', bnplApproved: true });
        await sendConfirmationEmails();
        setIsProcessing(false);
        onNext();
      }, 1500);
    } else {
      setTimeout(async () => {
        onUpdate({ paymentMethod });
        await sendConfirmationEmails();
        setIsProcessing(false);
        onNext();
      }, 3000);
    }
  };

  const sendConfirmationEmails = async () => {
    const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;
    const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
    const bookingTemplateId = import.meta.env.VITE_EMAILJS_BOOKING_TEMPLATE_ID;

    if (!publicKey || !serviceId) {
      console.error('EmailJS credentials missing');
      return;
    }

    const bookingReference = `DA${Date.now().toString().slice(-6)}`;
    const roomName = formData?.roomType?.replace('-', ' ')?.replace(/\b\w/g, l => l?.toUpperCase()) || 'N/A';

    // Generate the PDF URL pointing to our new /invoice route
    const baseUrl = window.location.origin + '/invoice';
    const params = new URLSearchParams({
      ref: bookingReference,
      name: `${formData.firstName} ${formData.lastName}`,
      email: formData.email || '',
      phone: formData.phone || '',
      checkin: formData.checkIn || '',
      checkout: formData.checkOut || '',
      room: roomName,
      guests: String(formData.guests || ''),
      total: String(bookingSummary?.total || '0'),
      download: 'true' // Trigger automatic download on landing
    });
    const pdfUrl = `${baseUrl}?${params.toString()}`;

    const baseParams = {
      guest_name: `${formData.firstName} ${formData.lastName}`,
      guest_email: formData.email || 'N/A',
      phone: formData.phone,
      checkin: formData.checkIn,
      checkout: formData.checkOut,
      guests: formData.guests,
      room: roomName,
      note: formData.specialRequests || 'None',
      booking_reference: bookingReference,
      total_amount: bookingSummary?.total?.toLocaleString('en-IN') || '0',
      pdf_url: pdfUrl // PASSING THE DYNAMIC LINK HERE
    };

    // 1. Send confirmation email to ADMIN
    try {
      console.log('Sending Admin Notification...');
      await emailjs.send(
        serviceId,
        bookingTemplateId,
        {
          ...baseParams,
          to_email: 'dandeliadventure.info@gmail.com',
          email: 'dandeliadventure.info@gmail.com',
          subject: "🚨 New Booking Received"
        },
        publicKey
      );
    } catch (adminErr) {
      console.error('Failed to send Admin booking notification:', adminErr);
    }

    // 2. Send confirmation email to GUEST
    if (formData.email && formData.email.trim() !== '') {
      try {
        console.log('Sending Guest Confirmation...');
        await emailjs.send(
          serviceId,
          bookingTemplateId,
          {
            ...baseParams,
            to_email: formData.email,
            email: formData.email,
            subject: "🌿 Your Booking is Confirmed"
          },
          publicKey
        );
      } catch (guestErr) {
        console.error('Failed to send Guest confirmation:', guestErr);
      }
    }

    onUpdate({ bookingReference });
  };

  const isFormValid = () => {
    if (paymentMethod === 'card') {
      return (formData?.cardNumber &&
        formData?.cardName &&
        formData?.expiryMonth &&
        formData?.expiryYear &&
        formData?.cvv && formData?.billingAddress);
    }
    if (paymentMethod === 'bnpl') {
      return formData?.bnplTermsAccepted;
    }
    return true;
  };

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">
          Payment Method
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <button
            onClick={() => setPaymentMethod('card')}
            className={`p-4 border-2 rounded-lg transition-organic ${paymentMethod === 'card' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
          >
            <div className="flex items-center space-x-3">
              <Icon name="CreditCard" size={20} className="text-primary" />
              <span className="font-medium text-card-foreground">Credit/Debit Card</span>
            </div>
          </button>

          <button
            onClick={() => setPaymentMethod('upi')}
            className={`p-4 border-2 rounded-lg transition-organic ${paymentMethod === 'upi' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
          >
            <div className="flex items-center space-x-3">
              <Icon name="Smartphone" size={20} className="text-primary" />
              <span className="font-medium text-card-foreground">UPI Payment</span>
            </div>
          </button>

          <button
            onClick={() => setPaymentMethod('netbanking')}
            className={`p-4 border-2 rounded-lg transition-organic ${paymentMethod === 'netbanking' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
          >
            <div className="flex items-center space-x-3">
              <Icon name="Building" size={20} className="text-primary" />
              <span className="font-medium text-card-foreground">Net Banking</span>
            </div>
          </button>

          <button
            onClick={() => {
              setPaymentMethod('bnpl');
              setShowBnplInfo(true);
            }}
            className={`p-4 border-2 rounded-lg transition-organic ${paymentMethod === 'bnpl' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
          >
            <div className="flex items-center space-x-3">
              <Icon name="Clock" size={20} className="text-primary" />
              <span className="font-medium text-card-foreground">Buy Now Pay Later</span>
            </div>
          </button>
        </div>

        {paymentMethod === 'card' && (
          <div className="space-y-4">
            <Input
              label="Card Number"
              type="text"
              placeholder="1234 5678 9012 3456"
              value={formData?.cardNumber}
              onChange={(e) => onUpdate({ cardNumber: e?.target?.value })}
              maxLength={19}
              required
            />
            <Input
              label="Cardholder Name"
              type="text"
              placeholder="Name as on card"
              value={formData?.cardName}
              onChange={(e) => onUpdate({ cardName: e?.target?.value })}
              required
            />
            <div className="grid grid-cols-3 gap-4">
              <Select
                label="Expiry Month"
                placeholder="MM"
                options={monthOptions}
                value={formData?.expiryMonth}
                onChange={(value) => onUpdate({ expiryMonth: value })}
                required
              />
              <Select
                label="Expiry Year"
                placeholder="YYYY"
                options={yearOptions}
                value={formData?.expiryYear}
                onChange={(value) => onUpdate({ expiryYear: value })}
                required
              />
              <Input
                label="CVV"
                type="text"
                placeholder="123"
                value={formData?.cvv}
                onChange={(e) => onUpdate({ cvv: e?.target?.value })}
                maxLength={4}
                required
              />
            </div>
          </div>
        )}

        {paymentMethod === 'upi' && (
          <div className="text-center py-8">
            <Icon name="QrCode" size={64} className="text-primary mx-auto mb-4" />
            <p className="text-card-foreground font-medium mb-2">Scan QR Code to Pay</p>
            <p className="text-muted-foreground text-sm">Use any UPI app to scan and pay ₹{bookingSummary?.total?.toLocaleString('en-IN')}</p>
          </div>
        )}

        {paymentMethod === 'netbanking' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {['SBI', 'HDFC', 'ICICI', 'Axis', 'Kotak', 'PNB', 'BOB', 'Others']?.map((bank) => (
              <button key={bank} className="p-3 border border-border rounded-lg hover:border-primary transition-micro text-sm font-medium text-card-foreground">{bank}</button>
            ))}
          </div>
        )}

        {paymentMethod === 'bnpl' && (
          <div className="space-y-4">
            <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
              <h4 className="font-medium text-card-foreground mb-2 flex items-center">
                <Icon name="Info" size={16} className="text-accent mr-2" />
                Buy Now Pay Later Information
              </h4>
              <p className="text-sm text-muted-foreground mb-3">By selecting this option, your booking will be confirmed immediately without upfront payment. You'll pay directly at the resort during your stay.</p>
              <div className="space-y-2">
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="bnplTerms"
                    className="mt-1 mr-2"
                    checked={formData?.bnplTermsAccepted || false}
                    onChange={(e) => onUpdate({ bnplTermsAccepted: e.target.checked })}
                  />
                  <label htmlFor="bnplTerms" className="text-sm text-muted-foreground">I understand that by choosing BNPL, I am committing to this booking and agree to pay the full amount during my stay.</label>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-muted rounded-lg p-4">
        <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
          <div className="flex items-center space-x-2"><Icon name="Shield" size={16} className="text-success" /><span>SSL Secured</span></div>
          <div className="flex items-center space-x-2"><Icon name="Lock" size={16} className="text-success" /><span>256-bit Encryption</span></div>
          <div className="flex items-center space-x-2"><Icon name="CheckCircle" size={16} className="text-success" /><span>PCI Compliant</span></div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-4">
        <h5 className="font-medium text-card-foreground mb-2">Cancellation Policy</h5>
        <ul className="text-sm text-muted-foreground space-y-1">
          <li>• Free cancellation up to 48 hours before check-in</li>
          <li>• 50% refund for cancellations 24-48 hours before check-in</li>
          <li>• No refund for cancellations within 24 hours</li>
        </ul>
      </div>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} iconName="ArrowLeft" iconPosition="left" disabled={isProcessing}>Back to Details</Button>
        <Button variant="default" onClick={handlePayment} disabled={!isFormValid() || isProcessing} loading={isProcessing} iconName={paymentMethod === 'bnpl' ? 'Check' : 'Lock'} iconPosition="left">
          {isProcessing ? (paymentMethod === 'bnpl' ? 'Confirming Booking...' : 'Processing Payment...') : (paymentMethod === 'bnpl' ? 'Confirm Booking' : `Pay ₹${bookingSummary?.total?.toLocaleString('en-IN')}`)}
        </Button>
      </div>
    </div>
  );
};

export default PaymentSection;